import React, {useEffect, useRef} from "react";
import * as d3 from "d3";

/**
 * Column chart for cumulative visit distribution.
 * Props:
 * - cumulativeVisitDistribution: number[] where index N is days, value is number of keys used at least N days
 * - columnWidth?: width of a single bar in px (default 10)
 */
export function CumulativeVisitChart({ cumulativeVisitDistribution, registeredKeyCount, columnWidth = 10 }: {
  cumulativeVisitDistribution: number[];
  registeredKeyCount: number;
  columnWidth?: number;
}) {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!cumulativeVisitDistribution || cumulativeVisitDistribution.length === 0) {
      return;
    }

    // Clear previous SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    // Set dimensions and margins
    const margin = { top: 30, right: 80, bottom: 70, left: 60 };
    const width = cumulativeVisitDistribution.length * columnWidth;
    const height = 400 - margin.top - margin.bottom;

    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Prepare data as pairs {days, count}
    const data = cumulativeVisitDistribution
        .map((count, days) => ({ days, count }));

    // Scales
    const x = d3
      .scaleBand<number>()
      .domain(data.map(d => d.days))
      .range([0, width])
      .padding(0.2);


    const maxCount = Math.max(d3.max(data, d => d.count) || 0, registeredKeyCount);
    const y = d3
      .scaleLinear()
      .domain([0, maxCount * 1.1]) // 10% headroom
      .range([height, 0]);

    // X axis
    const xAxis = d3.axisBottom(x).tickFormat((d: number) => String(d));
    svg
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(xAxis as any)
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.5em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-45)");

    // Y axis
    svg.append("g").call(d3.axisLeft(y));

    // Y axis label
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("y", 0 - margin.left)
      .attr("x", 0 - height / 2)
      .attr("dy", "1em")
      .style("text-anchor", "middle")
      .text("Keys used at least N days");

    // Bars
    svg
      .selectAll("rect")
      .data(data)
      .enter()
      .append("rect")
      .attr("x", d => x(d.days) ?? 0)
      .attr("y", d => y(d.count))
      .attr("width", x.bandwidth())
      .attr("height", d => height - y(d.count))
      .attr("fill", "grey")
      .append("title")
      .text(d => `Days: ${d.days}\nKeys: ${d.count}`);

    // Add horizontal line for max possible visit count
    svg.append("line")
        .attr("x1", 0)
        .attr("y1", y(registeredKeyCount))
        .attr("x2", width)
        .attr("y2", y(registeredKeyCount))
        .attr("stroke", "black")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "5,5");

    // Add desk count label on the right side of the chart
    svg.append("text")
        .attr("x", width + 5)
        .attr("y", y(registeredKeyCount) + 3)
        .attr("text-anchor", "start") // Left-align the text
        .style("fill", "black")
        .style("font-size", "12px")
        .text(`Keys: ${registeredKeyCount}`)
        .append("title")
        .text('Number of keys provided to JetBrains during the period.');
  }, [cumulativeVisitDistribution, columnWidth, registeredKeyCount]);

  return <svg ref={svgRef} style={{ maxWidth: "100%", overflow: "auto" }} />;
}
